package com.mgacreative.hotel.feature.room.ui

import androidx.compose.foundation.*
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.GridItemSpan
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.automirrored.filled.ArrowForward
import androidx.compose.material.icons.automirrored.filled.List
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.mgacreative.hotel.domain.model.Agency
import com.mgacreative.hotel.shared.ui.designsystem.components.verticalScrollbar
import com.mgacreative.hotel.shared.ui.designsystem.i18n.l10n
import kotlinx.datetime.Clock
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.TimeZone
import kotlinx.datetime.plus
import kotlinx.datetime.todayIn
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun RoomStatusScreen(
    viewModel: InUseRoomsViewModel = koinViewModel(),
    onNavigate: (String) -> Unit = {}
) {
    val roomStatuses by viewModel.roomStatuses.collectAsState()
    val isLoading by viewModel.isLoading.collectAsState()
    val agencies by viewModel.agencies.collectAsState()

    var selectedRoom by remember { mutableStateOf<RoomStatusDisplay?>(null) }
    var showQuickReserve by remember { mutableStateOf<RoomStatusDisplay?>(null) }
    var statusFilter by remember { mutableStateOf("ALL") } // ALL, EMPTY, FULL, RESERVED, MAINTENANCE
    var selectedFloor by remember { mutableStateOf("ALL") } // ALL, 1, 2, 3...
    var searchQuery by remember { mutableStateOf("") }

    LaunchedEffect(Unit) {
        viewModel.loadRoomStatuses()
    }

    val totalRooms = roomStatuses.size
    val emptyCount = roomStatuses.count { it.status == "EMPTY" }
    val fullCount = roomStatuses.count { it.status == "FULL" }
    val reservedCount = roomStatuses.count { it.status == "RESERVED" }
    val maintenanceCount = roomStatuses.count { it.status == "MAINTENANCE" }
    val occupancyRate = if (totalRooms > 0) ((fullCount.toDouble() / totalRooms) * 100).toInt() else 0

    val filteredRooms = roomStatuses.filter { room ->
        val floor = if (room.roomNumber.length > 2) room.roomNumber.dropLast(2) else "1"
        val matchesFloor = selectedFloor == "ALL" || floor == selectedFloor
        val matchesStatus = when (statusFilter) {
            "EMPTY" -> room.status == "EMPTY"
            "FULL" -> room.status == "FULL"
            "RESERVED" -> room.status == "RESERVED"
            "MAINTENANCE" -> room.status == "MAINTENANCE"
            else -> true
        }
        val matchesSearch = searchQuery.isBlank() ||
                room.roomNumber.contains(searchQuery, ignoreCase = true) ||
                room.guestName.contains(searchQuery, ignoreCase = true) ||
                room.roomType.contains(searchQuery, ignoreCase = true)
        matchesFloor && matchesStatus && matchesSearch
    }

    val groupedRooms = filteredRooms.groupBy {
        if (it.roomNumber.length > 2) it.roomNumber.dropLast(2) else "1"
    }

    val allFloors = roomStatuses.map {
        if (it.roomNumber.length > 2) it.roomNumber.dropLast(2) else "1"
    }.distinct().sortedBy { it.toIntOrNull() ?: 0 }

    Scaffold(
        containerColor = Color(0xFFF8FAFC),
        topBar = {
            Surface(
                color = Color.White,
                shadowElevation = 1.dp
            ) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 16.dp, vertical = 12.dp),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        IconButton(
                            onClick = { onNavigate("DashboardScreen") },
                            modifier = Modifier
                                .size(36.dp)
                                .background(Color(0xFFF1F5F9), CircleShape)
                        ) {
                            Icon(
                                Icons.AutoMirrored.Filled.ArrowBack,
                                contentDescription = l10n("Back"),
                                tint = Color(0xFF1E293B),
                                modifier = Modifier.size(18.dp)
                            )
                        }
                        Spacer(modifier = Modifier.width(12.dp))
                        Column {
                            Text(
                                text = "Oda Durumları (Room Rack)",
                                fontWeight = FontWeight.Bold,
                                fontSize = 18.sp,
                                color = Color(0xFF0F172A)
                            )
                            Text(
                                text = "Anlık Kat Planı, Müşteri ve Doluluk Takibi",
                                fontSize = 12.sp,
                                color = Color(0xFF64748B)
                            )
                        }
                    }

                    Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                        IconButton(
                            onClick = { viewModel.loadRoomStatuses() },
                            modifier = Modifier
                                .size(36.dp)
                                .background(Color(0xFFF1F5F9), CircleShape)
                        ) {
                            Icon(Icons.Default.Refresh, contentDescription = "Yenile", tint = Color(0xFF2563EB), modifier = Modifier.size(18.dp))
                        }

                        OutlinedButton(
                            onClick = { onNavigate("FloorPlanEditorScreen") },
                            colors = ButtonDefaults.outlinedButtonColors(
                                containerColor = Color(0xFFEFF6FF),
                                contentColor = Color(0xFF2563EB)
                            ),
                            border = BorderStroke(1.dp, Color(0xFF2563EB)),
                            shape = RoundedCornerShape(10.dp),
                            contentPadding = PaddingValues(horizontal = 12.dp, vertical = 8.dp)
                        ) {
                            Icon(Icons.Default.Layers, contentDescription = null, tint = Color(0xFF2563EB), modifier = Modifier.size(16.dp))
                            Spacer(modifier = Modifier.width(6.dp))
                            Text("Kat Krokisi", fontSize = 13.sp, fontWeight = FontWeight.SemiBold, color = Color(0xFF2563EB))
                        }

                        Button(
                            onClick = { onNavigate("CreateReservationScreen") },
                            colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF2563EB)),
                            shape = RoundedCornerShape(10.dp),
                            contentPadding = PaddingValues(horizontal = 14.dp, vertical = 8.dp)
                        ) {
                            Icon(Icons.Default.Add, contentDescription = null, tint = Color.White, modifier = Modifier.size(16.dp))
                            Spacer(modifier = Modifier.width(6.dp))
                            Text("Yeni Rezervasyon", fontSize = 13.sp, fontWeight = FontWeight.SemiBold, color = Color.White)
                        }
                    }
                }
            }
        },
        contentWindowInsets = WindowInsets(0.dp),
        bottomBar = {
            Surface(
                color = Color.White,
                shadowElevation = 8.dp,
                border = BorderStroke(1.dp, Color(0xFFE2E8F0))
            ) {
                Column {
                    RoomRackBottomNavBar(selectedTab = 3, onNavigate = onNavigate)
                }
            }
        }
    ) { paddingValues ->
        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(paddingValues)
        ) {
            if (isLoading && roomStatuses.isEmpty()) {
                Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                    CircularProgressIndicator(color = Color(0xFF2563EB))
                }
            } else {
                val gridState = androidx.compose.foundation.lazy.grid.rememberLazyGridState()
                LazyVerticalGrid(
                    state = gridState,
                    columns = GridCells.Adaptive(minSize = 150.dp),
                    modifier = Modifier.fillMaxSize().widthIn(max = 1400.dp).verticalScrollbar(gridState),
                    horizontalArrangement = Arrangement.spacedBy(10.dp),
                    verticalArrangement = Arrangement.spacedBy(10.dp),
                    contentPadding = PaddingValues(16.dp)
                ) {
                    // KPI Summary Cards Span
                    item(span = { GridItemSpan(maxLineSpan) }) {
                        Surface(
                            shape = RoundedCornerShape(16.dp),
                            color = Color.White,
                            border = BorderStroke(1.dp, Color(0xFFE2E8F0)),
                            shadowElevation = 1.dp,
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(14.dp),
                                horizontalArrangement = Arrangement.SpaceBetween,
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                // Toplam
                                RackStatPill(
                                    label = "Toplam",
                                    count = "$totalRooms Oda",
                                    color = Color(0xFF0F172A),
                                    bgColor = Color(0xFFF1F5F9)
                                )

                                // Boş
                                RackStatPill(
                                    label = "Boş",
                                    count = "$emptyCount Oda",
                                    color = Color(0xFF16A34A),
                                    bgColor = Color(0xFFDCFCE7)
                                )

                                // Dolu
                                RackStatPill(
                                    label = "Dolu",
                                    count = "$fullCount Oda",
                                    color = Color(0xFFDC2626),
                                    bgColor = Color(0xFFFEE2E2)
                                )

                                // Rezerve
                                RackStatPill(
                                    label = "Rezerve",
                                    count = "$reservedCount Oda",
                                    color = Color(0xFFD97706),
                                    bgColor = Color(0xFFFEF3C7)
                                )

                                if (maintenanceCount > 0) {
                                    RackStatPill(
                                        label = "Bakımda",
                                        count = "$maintenanceCount Oda",
                                        color = Color(0xFF64748B),
                                        bgColor = Color(0xFFF1F5F9)
                                    )
                                }

                                // Doluluk Oranı
                                Surface(
                                    shape = RoundedCornerShape(10.dp),
                                    color = Color(0xFFEFF6FF),
                                    border = BorderStroke(1.dp, Color(0xFFBFDBFE))
                                ) {
                                    Row(
                                        modifier = Modifier.padding(horizontal = 12.dp, vertical = 6.dp),
                                        verticalAlignment = Alignment.CenterVertically,
                                        horizontalArrangement = Arrangement.spacedBy(6.dp)
                                    ) {
                                        Text("Doluluk:", fontSize = 12.sp, fontWeight = FontWeight.Medium, color = Color(0xFF1E3A8A))
                                        Text("%$occupancyRate", fontSize = 14.sp, fontWeight = FontWeight.ExtraBold, color = Color(0xFF2563EB))
                                    }
                                }
                            }
                        }
                    }

                    // Search & Filters Span
                    item(span = { GridItemSpan(maxLineSpan) }) {
                        Surface(
                            shape = RoundedCornerShape(16.dp),
                            color = Color.White,
                            border = BorderStroke(1.dp, Color(0xFFE2E8F0)),
                            modifier = Modifier.fillMaxWidth().padding(top = 4.dp, bottom = 4.dp)
                        ) {
                            Column(modifier = Modifier.padding(12.dp), verticalArrangement = Arrangement.spacedBy(10.dp)) {
                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    horizontalArrangement = Arrangement.SpaceBetween,
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    // Search field
                                    OutlinedTextField(
                                        value = searchQuery,
                                        onValueChange = { searchQuery = it },
                                        placeholder = { Text("Oda no veya müşteri adı ara...", fontSize = 13.sp, color = Color(0xFF94A3B8)) },
                                        leadingIcon = {
                                            Icon(Icons.Default.Search, contentDescription = null, tint = Color(0xFF64748B), modifier = Modifier.size(18.dp))
                                        },
                                        trailingIcon = {
                                            if (searchQuery.isNotEmpty()) {
                                                IconButton(onClick = { searchQuery = "" }) {
                                                    Icon(Icons.Default.Clear, contentDescription = null, tint = Color(0xFF64748B), modifier = Modifier.size(16.dp))
                                                }
                                            }
                                        },
                                        singleLine = true,
                                        shape = RoundedCornerShape(10.dp),
                                        colors = OutlinedTextFieldDefaults.colors(
                                            focusedBorderColor = Color(0xFF2563EB),
                                            unfocusedBorderColor = Color(0xFFE2E8F0),
                                            focusedContainerColor = Color.White,
                                            unfocusedContainerColor = Color(0xFFF8FAFC)
                                        ),
                                        modifier = Modifier.weight(1f).height(44.dp)
                                    )

                                    Spacer(modifier = Modifier.width(12.dp))

                                    // Floor Filters
                                    Row(horizontalArrangement = Arrangement.spacedBy(4.dp)) {
                                        RackFilterChip(
                                            label = "Tüm Katlar",
                                            isSelected = selectedFloor == "ALL",
                                            onClick = { selectedFloor = "ALL" }
                                        )
                                        allFloors.forEach { fl ->
                                            RackFilterChip(
                                                label = "$fl. Kat",
                                                isSelected = selectedFloor == fl,
                                                onClick = { selectedFloor = fl }
                                            )
                                        }
                                    }
                                }

                                // Status Filters
                                Row(horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                                    RackFilterChip(
                                        label = "Tüm Odalar (${roomStatuses.size})",
                                        isSelected = statusFilter == "ALL",
                                        onClick = { statusFilter = "ALL" }
                                    )
                                    RackFilterChip(
                                        label = "Boş Odalar ($emptyCount)",
                                        isSelected = statusFilter == "EMPTY",
                                        selectedColor = Color(0xFF16A34A),
                                        onClick = { statusFilter = "EMPTY" }
                                    )
                                    RackFilterChip(
                                        label = "Dolu Odalar ($fullCount)",
                                        isSelected = statusFilter == "FULL",
                                        selectedColor = Color(0xFFDC2626),
                                        onClick = { statusFilter = "FULL" }
                                    )
                                    RackFilterChip(
                                        label = "Rezerve ($reservedCount)",
                                        isSelected = statusFilter == "RESERVED",
                                        selectedColor = Color(0xFFD97706),
                                        onClick = { statusFilter = "RESERVED" }
                                    )
                                    if (maintenanceCount > 0) {
                                        RackFilterChip(
                                            label = "Bakımda ($maintenanceCount)",
                                            isSelected = statusFilter == "MAINTENANCE",
                                            selectedColor = Color(0xFF64748B),
                                            onClick = { statusFilter = "MAINTENANCE" }
                                        )
                                    }
                                }
                            }
                        }
                    }

                    // Grouped Floors
                    groupedRooms.keys.sortedBy { it.toIntOrNull() ?: 0 }.forEach { floor ->
                        val roomsOnFloor = groupedRooms[floor] ?: emptyList()

                        item(span = { GridItemSpan(maxLineSpan) }) {
                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(top = 12.dp, bottom = 4.dp),
                                horizontalArrangement = Arrangement.SpaceBetween,
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Row(verticalAlignment = Alignment.CenterVertically) {
                                    Box(
                                        modifier = Modifier
                                            .size(8.dp)
                                            .clip(CircleShape)
                                            .background(Color(0xFF2563EB))
                                    )
                                    Spacer(modifier = Modifier.width(8.dp))
                                    Text(
                                        text = "$floor. KAT",
                                        fontSize = 14.sp,
                                        fontWeight = FontWeight.ExtraBold,
                                        color = Color(0xFF1E293B),
                                        letterSpacing = 0.5.sp
                                    )
                                }

                                Row(verticalAlignment = Alignment.CenterVertically) {
                                    Text(
                                        text = "${roomsOnFloor.size} Oda (${roomsOnFloor.count { it.status == "FULL" }} Dolu, ${roomsOnFloor.count { it.status == "EMPTY" }} Boş)",
                                        fontSize = 12.sp,
                                        color = Color(0xFF64748B),
                                        fontWeight = FontWeight.Medium
                                    )
                                    Spacer(modifier = Modifier.width(8.dp))
                                    OutlinedButton(
                                        onClick = { onNavigate("FloorPlanEditorScreen") },
                                        shape = RoundedCornerShape(6.dp),
                                        border = BorderStroke(1.dp, Color(0xFF2563EB)),
                                        colors = ButtonDefaults.outlinedButtonColors(
                                            containerColor = Color(0xFFEFF6FF),
                                            contentColor = Color(0xFF2563EB)
                                        ),
                                        contentPadding = PaddingValues(horizontal = 8.dp, vertical = 2.dp),
                                        modifier = Modifier.height(28.dp)
                                    ) {
                                        Icon(
                                            imageVector = Icons.Default.Layers,
                                            contentDescription = null,
                                            tint = Color(0xFF2563EB),
                                            modifier = Modifier.size(13.dp)
                                        )
                                        Spacer(modifier = Modifier.width(4.dp))
                                        Text(
                                            text = "Kat Krokisi",
                                            fontSize = 11.sp,
                                            fontWeight = FontWeight.SemiBold,
                                            color = Color(0xFF2563EB)
                                        )
                                    }
                                }
                            }
                        }

                        items(roomsOnFloor) { room ->
                            CompactRoomRackCard(room = room) {
                                selectedRoom = room
                            }
                        }

                        // Kat bloğu sağ alt köşe kroki düzenleme butonu
                        item(span = { GridItemSpan(maxLineSpan) }) {
                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(top = 4.dp, bottom = 12.dp),
                                horizontalArrangement = Arrangement.End
                            ) {
                                OutlinedButton(
                                    onClick = { onNavigate("FloorPlanEditorScreen") },
                                    shape = RoundedCornerShape(8.dp),
                                    border = BorderStroke(1.dp, Color(0xFF2563EB)),
                                    colors = ButtonDefaults.outlinedButtonColors(
                                        containerColor = Color(0xFFEFF6FF),
                                        contentColor = Color(0xFF2563EB)
                                    ),
                                    contentPadding = PaddingValues(horizontal = 12.dp, vertical = 6.dp)
                                ) {
                                    Icon(
                                        imageVector = Icons.Default.Layers,
                                        contentDescription = null,
                                        tint = Color(0xFF2563EB),
                                        modifier = Modifier.size(15.dp)
                                    )
                                    Spacer(modifier = Modifier.width(6.dp))
                                    Text(
                                        text = "$floor. Kat Krokisini Düzenle / Gör",
                                        fontSize = 12.sp,
                                        fontWeight = FontWeight.SemiBold,
                                        color = Color(0xFF2563EB)
                                    )
                                }
                            }
                        }
                    }

                    item(span = { GridItemSpan(maxLineSpan) }) {
                        Spacer(modifier = Modifier.height(40.dp))
                    }
                }
            }
        }
    }

    // Room Action & Detail Modal
    if (selectedRoom != null) {
        ModernRoomDetailDialog(
            room = selectedRoom!!,
            onDismiss = { selectedRoom = null },
            onReserveClick = {
                val r = selectedRoom
                selectedRoom = null
                showQuickReserve = r
            },
            onNavigate = onNavigate
        )
    }

    // Quick Reserve Modal
    if (showQuickReserve != null) {
        QuickReserveDialog(
            room = showQuickReserve!!,
            agencies = agencies,
            onDismiss = { showQuickReserve = null },
            onReserve = { agencyId, guestName, checkIn, checkOut ->
                viewModel.quickReserve(
                    roomId = showQuickReserve!!.id,
                    roomType = showQuickReserve!!.roomType,
                    agencyId = agencyId,
                    guestName = guestName,
                    checkIn = checkIn,
                    checkOut = checkOut
                )
                showQuickReserve = null
            }
        )
    }
}

@Composable
fun RackStatPill(label: String, count: String, color: Color, bgColor: Color) {
    Surface(
        shape = RoundedCornerShape(10.dp),
        color = bgColor
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 10.dp, vertical = 6.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(6.dp)
        ) {
            Box(modifier = Modifier.size(8.dp).clip(CircleShape).background(color))
            Text(label, fontSize = 11.sp, fontWeight = FontWeight.Medium, color = Color(0xFF475569))
            Text(count, fontSize = 12.sp, fontWeight = FontWeight.Bold, color = color)
        }
    }
}

@Composable
fun RackFilterChip(
    label: String,
    isSelected: Boolean,
    selectedColor: Color = Color(0xFF2563EB),
    onClick: () -> Unit
) {
    Surface(
        shape = RoundedCornerShape(8.dp),
        color = if (isSelected) selectedColor.copy(alpha = 0.12f) else Color(0xFFF1F5F9),
        border = BorderStroke(1.dp, if (isSelected) selectedColor else Color.Transparent),
        modifier = Modifier.clickable(onClick = onClick)
    ) {
        Text(
            text = label,
            fontSize = 12.sp,
            fontWeight = if (isSelected) FontWeight.Bold else FontWeight.Medium,
            color = if (isSelected) selectedColor else Color(0xFF475569),
            modifier = Modifier.padding(horizontal = 10.dp, vertical = 6.dp)
        )
    }
}

@Composable
fun CompactRoomRackCard(room: RoomStatusDisplay, onClick: () -> Unit) {
    val isFull = room.status == "FULL"
    val isReserved = room.status == "RESERVED"
    val isMaintenance = room.status == "MAINTENANCE"

    val statusColor = when {
        isFull -> Color(0xFFDC2626)
        isReserved -> Color(0xFFD97706)
        isMaintenance -> Color(0xFF64748B)
        else -> Color(0xFF16A34A)
    }

    val statusBg = when {
        isFull -> Color(0xFFFEE2E2)
        isReserved -> Color(0xFFFEF3C7)
        isMaintenance -> Color(0xFFF1F5F9)
        else -> Color(0xFFDCFCE7)
    }

    val statusText = when {
        isFull -> "Dolu"
        isReserved -> "Rezerve"
        isMaintenance -> "Bakımda"
        else -> "Boş"
    }

    Surface(
        shape = RoundedCornerShape(12.dp),
        color = Color.White,
        border = BorderStroke(1.dp, statusColor.copy(alpha = 0.35f)),
        shadowElevation = 1.dp,
        modifier = Modifier
            .fillMaxWidth()
            .height(86.dp)
            .clickable(onClick = onClick)
    ) {
        Box(modifier = Modifier.fillMaxSize()) {
            // Left Status Accent Bar
            Box(
                modifier = Modifier
                    .fillMaxHeight()
                    .width(4.dp)
                    .background(statusColor)
                    .align(Alignment.CenterStart)
            )

            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(start = 12.dp, top = 6.dp, end = 8.dp, bottom = 4.dp),
                verticalArrangement = Arrangement.SpaceBetween
            ) {
                // Header: Room Number & Status Pill
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(
                        text = room.roomNumber,
                        fontSize = 16.sp,
                        fontWeight = FontWeight.ExtraBold,
                        color = Color(0xFF0F172A),
                        lineHeight = 18.sp
                    )

                    Box(
                        modifier = Modifier
                            .clip(RoundedCornerShape(6.dp))
                            .background(statusBg)
                            .padding(horizontal = 6.dp, vertical = 2.dp)
                    ) {
                        Text(
                            text = statusText,
                            fontSize = 10.sp,
                            fontWeight = FontWeight.Bold,
                            color = statusColor,
                            lineHeight = 12.sp
                        )
                    }
                }

                // Middle/Bottom Content: Guest Name (if Full/Reserved) or Room Type (if Empty)
                Column(
                    modifier = Modifier.padding(bottom = 2.dp),
                    verticalArrangement = Arrangement.spacedBy(1.dp)
                ) {
                    if (isFull && room.guestName.isNotBlank()) {
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            Icon(
                                Icons.Default.Person,
                                contentDescription = null,
                                tint = Color(0xFFDC2626),
                                modifier = Modifier.size(11.dp)
                            )
                            Spacer(modifier = Modifier.width(3.dp))
                            Text(
                                text = room.guestName,
                                fontSize = 11.sp,
                                fontWeight = FontWeight.Bold,
                                color = Color(0xFF1E293B),
                                maxLines = 1,
                                overflow = TextOverflow.Ellipsis,
                                lineHeight = 13.sp
                            )
                        }
                        Text(
                            text = if (room.nights > 0) "${room.nights} Gece • ₺${room.totalCost}" else "Konaklamada",
                            fontSize = 10.sp,
                            color = Color(0xFF64748B),
                            maxLines = 1,
                            lineHeight = 12.sp
                        )
                    } else if (isReserved && room.guestName.isNotBlank()) {
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            Icon(
                                Icons.Default.DateRange,
                                contentDescription = null,
                                tint = Color(0xFFD97706),
                                modifier = Modifier.size(11.dp)
                            )
                            Spacer(modifier = Modifier.width(3.dp))
                            Text(
                                text = room.guestName,
                                fontSize = 11.sp,
                                fontWeight = FontWeight.Bold,
                                color = Color(0xFF1E293B),
                                maxLines = 1,
                                overflow = TextOverflow.Ellipsis,
                                lineHeight = 13.sp
                            )
                        }
                        Text(
                            text = l10n(room.roomType),
                            fontSize = 10.sp,
                            color = Color(0xFF64748B),
                            maxLines = 1,
                            lineHeight = 12.sp
                        )
                    } else {
                        // Empty / Maintenance
                        Text(
                            text = l10n(room.roomType),
                            fontSize = 11.sp,
                            fontWeight = FontWeight.SemiBold,
                            color = Color(0xFF334155),
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis,
                            lineHeight = 13.sp
                        )
                        Text(
                            text = if (isMaintenance) "Bakım Onarımda" else "Satışa Hazır",
                            fontSize = 10.sp,
                            color = if (isMaintenance) Color(0xFFDC2626) else Color(0xFF16A34A),
                            fontWeight = FontWeight.Medium,
                            lineHeight = 12.sp
                        )
                    }
                }
            }
        }
    }
}

@Composable
fun ModernRoomDetailDialog(
    room: RoomStatusDisplay,
    onDismiss: () -> Unit,
    onReserveClick: () -> Unit,
    onNavigate: (String) -> Unit
) {
    val isFull = room.status == "FULL"
    val isReserved = room.status == "RESERVED"

    AlertDialog(
        onDismissRequest = onDismiss,
        containerColor = Color.White,
        shape = RoundedCornerShape(20.dp),
        title = {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Box(
                        modifier = Modifier
                            .size(36.dp)
                            .clip(RoundedCornerShape(8.dp))
                            .background(Color(0xFFEFF6FF)),
                        contentAlignment = Alignment.Center
                    ) {
                        Icon(Icons.Default.Home, contentDescription = null, tint = Color(0xFF2563EB), modifier = Modifier.size(20.dp))
                    }
                    Spacer(modifier = Modifier.width(10.dp))
                    Column {
                        Text("Oda ${room.roomNumber}", fontWeight = FontWeight.Bold, fontSize = 18.sp, color = Color(0xFF0F172A))
                        Text(l10n(room.roomType), fontSize = 12.sp, color = Color(0xFF64748B))
                    }
                }

                IconButton(onClick = onDismiss, modifier = Modifier.size(28.dp)) {
                    Icon(Icons.Default.Close, contentDescription = "Kapat", tint = Color(0xFF64748B))
                }
            }
        },
        text = {
            Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                // Info Box
                Surface(
                    shape = RoundedCornerShape(12.dp),
                    color = Color(0xFFF8FAFC),
                    border = BorderStroke(1.dp, Color(0xFFE2E8F0)),
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Column(modifier = Modifier.padding(14.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                        Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                            Text("Oda Durumu:", fontSize = 12.sp, color = Color(0xFF64748B))
                            Text(
                                text = when (room.status) {
                                    "FULL" -> "Dolu (Konaklamada)"
                                    "RESERVED" -> "Rezerve"
                                    "MAINTENANCE" -> "Bakımda"
                                    else -> "Boş (Kullanıma Hazır)"
                                },
                                fontWeight = FontWeight.Bold,
                                fontSize = 12.sp,
                                color = when (room.status) {
                                    "FULL" -> Color(0xFFDC2626)
                                    "RESERVED" -> Color(0xFFD97706)
                                    else -> Color(0xFF16A34A)
                                }
                            )
                        }

                        if (room.guestName.isNotBlank()) {
                            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                                Text("Konaklayan Misafir:", fontSize = 12.sp, color = Color(0xFF64748B))
                                Text(room.guestName, fontWeight = FontWeight.Bold, fontSize = 12.sp, color = Color(0xFF0F172A))
                            }
                        }

                        if (isFull) {
                            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                                Text("Konaklama Süresi:", fontSize = 12.sp, color = Color(0xFF64748B))
                                Text("${room.nights} Gece", fontWeight = FontWeight.Bold, fontSize = 12.sp, color = Color(0xFF0F172A))
                            }
                            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                                Text("Toplam Hesap / Folyo:", fontSize = 12.sp, color = Color(0xFF64748B))
                                Text("₺ ${room.totalCost}", fontWeight = FontWeight.ExtraBold, fontSize = 13.sp, color = Color(0xFF2563EB))
                            }
                        }
                    }
                }

                // Quick Actions
                if (room.status == "EMPTY") {
                    Button(
                        onClick = onReserveClick,
                        colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF2563EB)),
                        shape = RoundedCornerShape(10.dp),
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        Icon(Icons.Default.Add, contentDescription = null, tint = Color.White, modifier = Modifier.size(16.dp))
                        Spacer(modifier = Modifier.width(6.dp))
                        Text("Hızlı Rezervasyon Yap", color = Color.White, fontWeight = FontWeight.Bold)
                    }
                } else if (isFull) {
                    Button(
                        onClick = {
                            onDismiss()
                            onNavigate("CheckOutListScreen")
                        },
                        colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF16A34A)),
                        shape = RoundedCornerShape(10.dp),
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        Icon(Icons.Default.ExitToApp, contentDescription = null, tint = Color.White, modifier = Modifier.size(16.dp))
                        Spacer(modifier = Modifier.width(6.dp))
                        Text("Check-Out / Hesap Kapat", color = Color.White, fontWeight = FontWeight.Bold)
                    }
                }
            }
        },
        confirmButton = {},
        dismissButton = {
            TextButton(onClick = onDismiss, shape = RoundedCornerShape(10.dp)) {
                Text("Kapat", color = Color(0xFF64748B))
            }
        }
    )
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun QuickReserveDialog(
    room: RoomStatusDisplay,
    agencies: List<Agency>,
    onDismiss: () -> Unit,
    onReserve: (agencyId: String?, guestName: String, checkIn: String, checkOut: String) -> Unit
) {
    var guestName by remember { mutableStateOf("") }
    var selectedAgencyId by remember { mutableStateOf<String?>(null) }
    var agencyExpanded by remember { mutableStateOf(false) }

    val today = Clock.System.todayIn(TimeZone.currentSystemDefault())
    val tomorrow = today.plus(DatePeriod(days = 1))

    val checkIn = today.toString()
    val checkOut = tomorrow.toString()

    AlertDialog(
        onDismissRequest = onDismiss,
        containerColor = Color.White,
        shape = RoundedCornerShape(20.dp),
        title = {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text("Hızlı Rezervasyon - Oda ${room.roomNumber}", fontWeight = FontWeight.Bold, fontSize = 18.sp, color = Color(0xFF0F172A))
                IconButton(onClick = onDismiss, modifier = Modifier.size(28.dp)) {
                    Icon(Icons.Default.Close, contentDescription = "Kapat", tint = Color(0xFF64748B))
                }
            }
        },
        text = {
            Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                // Agency Dropdown
                ExposedDropdownMenuBox(
                    expanded = agencyExpanded,
                    onExpandedChange = { agencyExpanded = !agencyExpanded }
                ) {
                    val agencyName = agencies.find { it.id == selectedAgencyId }?.name ?: "Acente Seçin (Opsiyonel / Münferit)"
                    OutlinedTextField(
                        value = agencyName,
                        onValueChange = {},
                        readOnly = true,
                        label = { Text("Kanal / Acente", fontSize = 12.sp) },
                        trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = agencyExpanded) },
                        shape = RoundedCornerShape(12.dp),
                        colors = OutlinedTextFieldDefaults.colors(
                            focusedBorderColor = Color(0xFF2563EB),
                            unfocusedBorderColor = Color(0xFFCBD5E1),
                            focusedContainerColor = Color.White,
                            unfocusedContainerColor = Color(0xFFF8FAFC)
                        ),
                        modifier = Modifier.menuAnchor().fillMaxWidth()
                    )
                    ExposedDropdownMenu(
                        expanded = agencyExpanded,
                        onDismissRequest = { agencyExpanded = false },
                        modifier = Modifier.background(Color.White)
                    ) {
                        DropdownMenuItem(
                            text = { Text("Münferit Misafir (Acente Yok)", fontSize = 13.sp, color = Color(0xFF0F172A)) },
                            onClick = {
                                selectedAgencyId = null
                                agencyExpanded = false
                            }
                        )
                        agencies.forEach { agency ->
                            DropdownMenuItem(
                                text = { Text(agency.name, fontSize = 13.sp, color = Color(0xFF0F172A)) },
                                onClick = {
                                    selectedAgencyId = agency.id
                                    agencyExpanded = false
                                }
                            )
                        }
                    }
                }

                // Guest Name
                OutlinedTextField(
                    value = guestName,
                    onValueChange = { guestName = it },
                    label = { Text("Misafir Adı Soyadı", fontSize = 12.sp) },
                    placeholder = { Text("Örn: Ahmet Yılmaz", color = Color(0xFF94A3B8)) },
                    singleLine = true,
                    shape = RoundedCornerShape(12.dp),
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedBorderColor = Color(0xFF2563EB),
                        unfocusedBorderColor = Color(0xFFCBD5E1),
                        focusedContainerColor = Color.White,
                        unfocusedContainerColor = Color(0xFFF8FAFC)
                    ),
                    modifier = Modifier.fillMaxWidth()
                )

                // Date Information Box
                Surface(
                    shape = RoundedCornerShape(10.dp),
                    color = Color(0xFFEFF6FF),
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Row(
                        modifier = Modifier.padding(10.dp),
                        horizontalArrangement = Arrangement.SpaceBetween
                    ) {
                        Text("Giriş: $checkIn", fontSize = 12.sp, fontWeight = FontWeight.Bold, color = Color(0xFF1E3A8A))
                        Text("Çıkış: $checkOut", fontSize = 12.sp, fontWeight = FontWeight.Bold, color = Color(0xFF1E3A8A))
                    }
                }
            }
        },
        confirmButton = {
            Button(
                onClick = { onReserve(selectedAgencyId, guestName, checkIn, checkOut) },
                enabled = guestName.isNotBlank() || selectedAgencyId != null,
                colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF2563EB)),
                shape = RoundedCornerShape(10.dp),
                contentPadding = PaddingValues(horizontal = 20.dp, vertical = 10.dp)
            ) {
                Text("Kaydet & Odayı Doldur", fontWeight = FontWeight.Bold, color = Color.White)
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss, shape = RoundedCornerShape(10.dp)) {
                Text("İptal", color = Color(0xFF64748B))
            }
        }
    )
}

@Composable
fun RoomRackBottomNavBar(selectedTab: Int, onNavigate: (String) -> Unit) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(vertical = 8.dp, horizontal = 16.dp),
        horizontalArrangement = Arrangement.SpaceAround,
        verticalAlignment = Alignment.CenterVertically
    ) {
        RoomRackNavItem(
            title = "Rezervasyonlar",
            icon = Icons.AutoMirrored.Filled.List,
            isSelected = selectedTab == 0,
            onClick = { onNavigate("NewReservationCalendarScreen") }
        )

        RoomRackNavItem(
            title = "Takvim (Doluluk)",
            icon = Icons.Default.DateRange,
            isSelected = selectedTab == 1,
            onClick = { onNavigate("ReservationCalendarScreen") }
        )

        RoomRackNavItem(
            title = "Giriş / Çıkış",
            icon = Icons.AutoMirrored.Filled.ArrowForward,
            isSelected = selectedTab == 2,
            onClick = { onNavigate("CheckInListScreen") }
        )

        RoomRackNavItem(
            title = "Oda Durumları",
            icon = Icons.Default.Home,
            isSelected = selectedTab == 3,
            onClick = { onNavigate("RoomStatusScreen") }
        )

        RoomRackNavItem(
            title = "Dolu Odalar",
            icon = Icons.Default.Person,
            isSelected = selectedTab == 4,
            onClick = { onNavigate("InUseRoomsScreen") }
        )
    }
}

@Composable
fun RoomRackNavItem(
    title: String,
    icon: androidx.compose.ui.graphics.vector.ImageVector,
    isSelected: Boolean,
    onClick: () -> Unit
) {
    val activeColor = Color(0xFF2563EB)
    val inactiveColor = Color(0xFF64748B)

    Surface(
        shape = RoundedCornerShape(12.dp),
        color = if (isSelected) Color(0xFFEFF6FF) else Color.Transparent,
        modifier = Modifier.clickable(onClick = onClick)
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 12.dp, vertical = 8.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(6.dp)
        ) {
            Icon(
                imageVector = icon,
                contentDescription = title,
                tint = if (isSelected) activeColor else inactiveColor,
                modifier = Modifier.size(18.dp)
            )
            Text(
                text = title,
                fontSize = 12.sp,
                fontWeight = if (isSelected) FontWeight.Bold else FontWeight.Medium,
                color = if (isSelected) activeColor else inactiveColor
            )
        }
    }
}
